import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useMedia } from "react-use";
import { Button, RocketIcon, Tooltip, IconButton, GiftIcon } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import resolveConfig from "tailwindcss/resolveConfig";
import { getIsSuspendedTrial } from "~/store/selectors/selector.accountSettings";
import { getIsTrial, makeGetCurrentPlanName } from "~/store/selectors/selector.configOptions";
import twConfig from "../../../tailwind.config";

const BuyNowButton = ({ url, className, resizable, iconOnly, discount }) => {
  const { t } = useTranslation();
  const isTrial = useSelector((state) => getIsTrial(state));
  const isSuspendedTrial = useSelector(getIsSuspendedTrial);
  const getCurrentPlanName = makeGetCurrentPlanName();
  const currentPlanName = useSelector((state) => getCurrentPlanName(state));
  const tailwindConfig = resolveConfig(twConfig);
  const isShort = useMedia(`(max-width: ${tailwindConfig.theme.screens.xl})`);

  const withText = useMemo(() => {
    if (!resizable) {
      return true;
    }

    if (isShort) {
      return false;
    }

    return !iconOnly;
  }, [iconOnly, isShort, resizable]);

  const label = useMemo(() => {
    if (isTrial || isSuspendedTrial) {
      return t("Activate");
    }

    return currentPlanName.includes("app_sumo") ? t("Lifetime") : t("Upgrade");
  }, [currentPlanName, isSuspendedTrial, isTrial, t]);

  const renderButton = useMemo(() => {
    if (
      isTrial ||
      isSuspendedTrial ||
      (!isTrial && (currentPlanName.includes("app_sumo") || currentPlanName.includes("free")))
    ) {
      return withText ? (
        <Link to={url}>
          <Button size="big">
            {discount ? <GiftIcon /> : <RocketIcon />}
            {label}
          </Button>
        </Link>
      ) : (
        <Link to={url}>
          <Tooltip
            title={label}
            placement="right"
          >
            <IconButton size="big">{discount ? <GiftIcon /> : <RocketIcon />}</IconButton>
          </Tooltip>
        </Link>
      );
    }
  }, [currentPlanName, discount, isSuspendedTrial, isTrial, label, url, withText]);

  return <div className={classNames("buy_now_button__holder", className)}>{renderButton}</div>;
};

BuyNowButton.propTypes = {
  url: PropTypes.string.isRequired,
  className: PropTypes.string,
  resizable: PropTypes.bool,
  iconOnly: PropTypes.bool,
  discount: PropTypes.bool,
};

BuyNowButton.defaultProps = {
  resizable: false,
  iconOnly: false,
  discount: false,
};

BuyNowButton.displayName = `BuyNowButton`;

export default BuyNowButton;
